import { PitcherEventName, usePitcherApi } from '@sdk/main'

import { DEFAULT_PEER_CONNECTIVITY_VERSION, PEER_CONNECTIVITY_HANDLER_MATCH_ALL } from './peerConnectivity.constants'
import type {
  PeerConnectivityDispatchOptions,
  PeerConnectivityEvent,
  PeerConnectivityEventHandler,
  PeerConnectivityEventPayload,
} from './peerConnectivity.types'

type HandlerMap = Map<string, Set<PeerConnectivityEventHandler>>

const handlers: HandlerMap = new Map()
let unsubscribeNativeListener: (() => void) | null = null
let peerConnectivitySupportPromise: Promise<boolean> | null = null

function generateCorrelationId(): string {
  const cryptoApi = globalThis.crypto
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID()
  }
  return `peer-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

function normalizeEventPayload(
  payload: PeerConnectivityEventPayload,
  defaults?: Partial<PeerConnectivityEvent>,
): PeerConnectivityEvent {
  const metadata = payload.metadata ?? {}
  const normalized: PeerConnectivityEvent = {
    version: payload.version ?? defaults?.version ?? DEFAULT_PEER_CONNECTIVITY_VERSION,
    action: payload.action,
    context: payload.context ?? defaults?.context,
    data: payload.data ?? defaults?.data,
    metadata: {
      correlationId: metadata.correlationId ?? defaults?.metadata?.correlationId ?? generateCorrelationId(),
      source: metadata.source ?? defaults?.metadata?.source ?? 'web',
      timestamp: metadata.timestamp ?? defaults?.metadata?.timestamp ?? Date.now(),
      extras: {
        ...(defaults?.metadata?.extras ?? {}),
        ...(metadata.extras ?? {}),
      },
    },
  }

  return normalized
}

function addHandler(action: string, handler: PeerConnectivityEventHandler) {
  const key = action || PEER_CONNECTIVITY_HANDLER_MATCH_ALL
  const handlerSet = handlers.get(key) ?? new Set<PeerConnectivityEventHandler>()
  handlerSet.add(handler)
  handlers.set(key, handlerSet)
}

function removeHandler(action: string, handler: PeerConnectivityEventHandler) {
  const handlerSet = handlers.get(action)
  if (!handlerSet) {
    return
  }
  handlerSet.delete(handler)
  if (!handlerSet.size) {
    handlers.delete(action)
  }
  if (!handlers.size) {
    teardownNativeListener()
  }
}

async function ensureNativeListener() {
  if (unsubscribeNativeListener) {
    return
  }

  const api = usePitcherApi()
  const listener = (event: PeerConnectivityEventPayload) => {
    try {
      const normalized = normalizeEventPayload(event, {
        metadata: {
          correlationId: generateCorrelationId(),
          source: 'native',
          timestamp: Date.now(),
          extras: {},
        },
      })
      dispatchEvent(normalized)
    } catch (error) {
      console.error('[peer-connectivity] Failed to handle native event:', error)
    }
  }

  api.on(PitcherEventName.PEER_CONNECTIVITY_EVENT, listener as any)
  unsubscribeNativeListener = () => {
    api.off(PitcherEventName.PEER_CONNECTIVITY_EVENT, listener as any)
    unsubscribeNativeListener = null
  }
}

function teardownNativeListener() {
  if (unsubscribeNativeListener) {
    unsubscribeNativeListener()
    unsubscribeNativeListener = null
  }
}

function dispatchEvent(event: PeerConnectivityEvent) {
  const specificHandlers = handlers.get(event.action)
  const wildcardHandlers = handlers.get(PEER_CONNECTIVITY_HANDLER_MATCH_ALL)

  if (!specificHandlers && !wildcardHandlers) {
    return
  }

  const payload = Object.freeze(event)
  specificHandlers?.forEach((handler) => {
    try {
      handler(payload)
    } catch (error) {
      console.error(`[peer-connectivity] Handler error for action "${event.action}":`, error)
    }
  })
  wildcardHandlers?.forEach((handler) => {
    try {
      handler(payload)
    } catch (error) {
      console.error('[peer-connectivity] Handler error for wildcard action:', error)
    }
  })
}

async function isPeerConnectivitySupported(): Promise<boolean> {
  if (!peerConnectivitySupportPromise) {
    const api = usePitcherApi()
    peerConnectivitySupportPromise = api
      .getEnv()
      .then((env) => env.mode === 'IOS')
      .catch((error) => {
        console.error('[peer-connectivity] Failed to determine environment support:', error)
        return false
      })
  }

  return peerConnectivitySupportPromise
}

function normalizeActions(actionOrActions: string | string[]): string[] {
  return Array.isArray(actionOrActions) ? actionOrActions : [actionOrActions]
}

export function registerPeerConnectivityHandler(
  actionOrActions: string | string[],
  handler: PeerConnectivityEventHandler,
): () => void {
  normalizeActions(actionOrActions).forEach((action) => {
    addHandler(action, handler)
  })

  ensureNativeListener().catch((error) => {
    console.error('[peer-connectivity] Failed to register native listener:', error)
  })

  return () => {
    normalizeActions(actionOrActions).forEach((action) => {
      removeHandler(action, handler)
    })
  }
}

export async function sendPeerConnectivityEvent(
  payload: PeerConnectivityEventPayload,
  options?: PeerConnectivityDispatchOptions,
): Promise<void> {
  if (!payload?.action) {
    console.warn('[peer-connectivity] Attempted to send an event without action')
    return
  }

  if (options?.isFeatureEnabled === false) {
    return
  }

  const isSupported = await isPeerConnectivitySupported()
  if (!isSupported) {
    return
  }

  const api = usePitcherApi()
  const normalizedPayload = normalizeEventPayload(payload, {
    metadata: {
      correlationId: generateCorrelationId(),
      source: 'web',
      timestamp: Date.now(),
      extras: {},
    },
  })

  try {
    await api.broadcast({
      type: PitcherEventName.PEER_CONNECTIVITY_EVENT,
      body: normalizedPayload,
    })
  } catch (error) {
    console.error('[peer-connectivity] Failed to send event:', error)
  }
}
